import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  XCircleIcon,
  PencilIcon,
  CheckIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";

export default function LeadDetail({ lead, onClose, refreshLeads }) {
  const [formData, setFormData] = useState(lead);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transformed, setTransformed] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const navigate = useNavigate();

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  useEffect(() => {
    setFormData(lead);
  }, [lead]);

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/leads/${formData._id}`, formData);
      showToast("Informations mises à jour !");
      refreshLeads();
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la mise à jour.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTransform = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${BACKEND_URL}/leads/transform/${formData._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(response.data.message || "Transformation réussie !");
      setTransformed(true);
      refreshLeads();
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Erreur lors de la transformation.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 max-h-[95vh] overflow-y-auto">
          {/* Fermeture */}
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-gray-900">
            <XCircleIcon className="w-6 h-6" />
          </button>

          <DialogTitle className="text-xl font-semibold mb-4 text-center">
            {isEditing ? "Modifier le Lead" : "Détails du Lead"}
          </DialogTitle>

          <div className="space-y-4">
            {/* Champs du formulaire */}
            <div>
              <label className="block text-sm font-medium">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
              >
                <option value="Personne Physique">Personne Physique</option>
                <option value="Personne Morale">Personne Morale</option>
              </select>
            </div>

            <div className="flex gap-2">
              <div className="w-1/2">
                <label className="block text-sm font-medium">Nom</label>
                <input
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                />
              </div>
              <div className="w-1/2">
                <label className="block text-sm font-medium">Prénom</label>
                <input
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <div className="w-1/2">
                <label className="block text-sm font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                />
              </div>
              <div className="w-1/2">
                <label className="block text-sm font-medium">Téléphone</label>
                <input
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium">Adresse</label>
              <input
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Source d’acquisition</label>
              <input
                name="sourceAcquisition"
                value={formData.sourceAcquisition}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
              />
            </div>

            {formData.type === "Personne Morale" && (
              <>
                <div>
                  <label className="block text-sm font-medium">Statut Juridique</label>
                  <input
                    name="statutJuridique"
                    value={formData.statutJuridique}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">SIRET</label>
                  <input
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Secteur d’activité</label>
                  <input
                    name="secteurActivite"
                    value={formData.secteurActivite}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium">Commentaire</label>
              <textarea
                name="commentaire"
                value={formData.commentaire}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full border rounded-lg p-2 ${!isEditing && "bg-gray-100"}`}
              />
            </div>

            {/* Boutons */}
            <div className="flex justify-between mt-4">
              {isEditing ? (
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                >
                  <CheckIcon className="w-5 h-5" />
                  {loading ? "Enregistrement..." : "Sauvegarder"}
                </button>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
                >
                  <PencilIcon className="w-5 h-5" />
                  Modifier
                </button>
              )}

              {!isEditing && !transformed && (
                <button
                  onClick={handleTransform}
                  disabled={loading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  <ArrowsRightLeftIcon className="w-5 h-5" />
                  {loading ? "Transformation..." : "Transformer"}
                </button>
              )}

              {transformed && (
                <button
                  onClick={() => navigate("/prospects/prospects")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
                >
                  Aller à la page Prospects
                </button>
              )}
            </div>
          </div>
        </DialogPanel>
      </div>

      {/* ✅ Notification animée */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
          >
            <div
              className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${
                notification.type === "success"
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <div className="shrink-0">
                {notification.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-gray-700">
                <p className="font-semibold">
                  {notification.type === "success" ? "Succès" : "Erreur"}
                </p>
                <p className="text-sm">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
